// ----------------------------------------------------------------------------
// RestoreOrganizationGate
// Blocks the admin app when the current church has been soft-deleted.
// - Shows a full-screen modal explaining the org is scheduled for deletion.
// - Surfaces the projected purge date (deletedAt + RECOVERY_WINDOW_DAYS).
// - Lets any admin restore via POST /churches/{id}/restore.
// - Lets any admin skip the recovery window via DELETE /churches/{id}/permanent
//   after a name-typed confirmation.
// - Otherwise the user can sign out (the app blocks all interaction underneath).
// Mount inside ChurchProvider (e.g. AdminLayoutInner) so useChurch is available.
// ----------------------------------------------------------------------------

import { ReactNode, useMemo, useState } from 'react';
import { useAuth } from '@app/auth/AuthContext';
import { useChurch } from '@admin/shared/contexts/ChurchContext';
import { churchesApi } from '@app/api/organization';
import { Icons } from '@admin/shared/components/Icons';
import { Button, Input } from '@admin/shared/components/ui';
import { logger } from '@app/utils/logger';

interface Props {
  children: ReactNode;
}

/**
 * Days a soft-deleted church remains recoverable before it is permanently purged.
 * Kept as a frontend constant for display; backend purge job (when introduced)
 * should use the same value.
 */
export const RECOVERY_WINDOW_DAYS = 30;

/**
 * Returns true when the church is in a soft-deleted state.
 * A church is considered deleted if `isActive === false` OR `deletedAt` is populated.
 * Both conditions are checked because backend currently sets both on delete and
 * clears both on restore — but we don't want a partial state to slip through.
 */
export const isChurchSoftDeleted = (
  church: { isActive?: boolean; deletedAt?: string | null } | null | undefined
): boolean => {
  if (!church) return false;
  if (church.isActive === false) return true;
  if (church.deletedAt) return true;
  return false;
};

/**
 * Returns the projected permanent-purge date for a soft-deleted church, or null
 * if `deletedAt` is missing/unparseable.
 */
export const getPurgeDate = (
  deletedAt: string | null | undefined,
  recoveryWindowDays: number = RECOVERY_WINDOW_DAYS
): Date | null => {
  if (!deletedAt) return null;
  const parsed = new Date(deletedAt);
  if (Number.isNaN(parsed.getTime())) return null;
  const purge = new Date(parsed);
  purge.setUTCDate(purge.getUTCDate() + recoveryWindowDays);
  return purge;
};

const formatPurgeDate = (date: Date): string =>
  date.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });

export const RestoreOrganizationGate: React.FC<Props> = ({ children }) => {
  const { church, isLoading, refetchChurch } = useChurch();
  const { logout } = useAuth();
  const [isRestoring, setIsRestoring] = useState(false);
  const [isPurging, setIsPurging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPurgeConfirm, setShowPurgeConfirm] = useState(false);
  const [purgeConfirmName, setPurgeConfirmName] = useState('');

  const purgeDate = useMemo(
    () => getPurgeDate(church?.deletedAt ?? null),
    [church?.deletedAt]
  );

  // Wait for church bootstrap before deciding what to show.
  if (isLoading) return <>{children}</>;

  if (!isChurchSoftDeleted(church) || !church) {
    return <>{children}</>;
  }

  const handleRestore = async () => {
    setError(null);
    setIsRestoring(true);
    try {
      await churchesApi.restore(church.id);
      await refetchChurch();
    } catch (err: any) {
      logger.error('Failed to restore organization', err);
      setError(err?.message || 'Failed to restore the organization. Please try again.');
    } finally {
      setIsRestoring(false);
    }
  };

  const handleSignOut = async () => {
    try {
      await logout();
    } finally {
      window.location.href = '/login';
    }
  };

  const canConfirmPurge = purgeConfirmName === church.name;

  const handlePermanentDelete = async () => {
    if (!canConfirmPurge) return;
    setError(null);
    setIsPurging(true);
    try {
      await churchesApi.permanentlyDelete(church.id);
      // Once the church is gone there is nothing to render — sign the user out.
      try {
        await logout();
      } finally {
        window.location.href = '/login';
      }
    } catch (err: any) {
      logger.error('Failed to permanently delete organization', err);
      setError(err?.message || 'Failed to permanently delete the organization. Please try again.');
      setIsPurging(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-gray-900/70 backdrop-blur-sm p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="restore-org-title"
      data-testid="restore-organization-gate"
    >
      <div className="max-w-lg w-full bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-amber-300 dark:border-amber-700 p-6 space-y-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-amber-100 dark:bg-amber-900/40 flex items-center justify-center">
            <Icons.AlertTriangle className="w-6 h-6 text-amber-600 dark:text-amber-300" />
          </div>
          <div>
            <h2 id="restore-org-title" className="text-lg font-semibold text-gray-900 dark:text-white">
              {church.name} is scheduled for deletion
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">Soft delete, recoverable until permanent purge.</p>
          </div>
        </div>

        {purgeDate && (
          <div
            className="flex items-center gap-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3"
            data-testid="purge-date-banner"
          >
            <Icons.CalendarDays className="w-5 h-5 text-amber-700 dark:text-amber-300 shrink-0" />
            <div className="text-sm text-amber-900 dark:text-amber-200">
              Permanent deletion scheduled for{' '}
              <strong data-testid="purge-date">{formatPurgeDate(purgeDate)}</strong>
              <span className="text-amber-800/80 dark:text-amber-300/80"> ({RECOVERY_WINDOW_DAYS}-day recovery window).</span>
            </div>
          </div>
        )}

        <div className="text-sm text-gray-700 dark:text-gray-300 space-y-2">
          <p>
            This organization was deleted by an admin and is currently hidden from members.
            All members, songs, and service plans are preserved during the recovery window.
          </p>
          <p>
            You can restore it now to bring everything back online, sign out and decide later,
            or skip the recovery window and delete it permanently right now.
          </p>
        </div>

        {error && (
          <div className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
            {error}
          </div>
        )}

        {showPurgeConfirm && (
          <div
            className="border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/20 rounded-lg p-3 space-y-2"
            data-testid="purge-confirm-panel"
          >
            <p className="text-sm text-red-800 dark:text-red-200 font-medium">
              This permanently deletes the organization and all of its data. This cannot be undone.
            </p>
            <Input
              label={<>Type <strong>&ldquo;{church.name}&rdquo;</strong> to confirm</>}
              value={purgeConfirmName}
              onChange={(e) => setPurgeConfirmName(e.target.value)}
              placeholder={church.name}
              data-testid="purge-confirm-input"
            />
            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-1">
              <Button
                variant="secondary"
                size="sm"
                className="w-full sm:w-auto whitespace-nowrap"
                onClick={() => {
                  setShowPurgeConfirm(false);
                  setPurgeConfirmName('');
                }}
                disabled={isPurging}
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                size="sm"
                className="w-full sm:w-auto whitespace-nowrap"
                onClick={handlePermanentDelete}
                disabled={!canConfirmPurge || isPurging}
                data-testid="confirm-permanent-delete-button"
              >
                {isPurging ? 'Deleting…' : 'Delete forever'}
              </Button>
            </div>
          </div>
        )}

        <div className="flex flex-col gap-2 pt-2">
          <Button
            variant="primary"
            onClick={handleRestore}
            disabled={isRestoring || isPurging}
            className="w-full whitespace-nowrap"
            data-testid="restore-organization-button"
          >
            {isRestoring ? 'Restoring…' : 'Restore organization'}
          </Button>
          <Button
            variant="secondary"
            onClick={handleSignOut}
            disabled={isRestoring || isPurging}
            className="w-full whitespace-nowrap"
          >
            Sign out
          </Button>
          <Button
            variant="danger"
            onClick={() => setShowPurgeConfirm(true)}
            disabled={isRestoring || isPurging || showPurgeConfirm}
            className="w-full whitespace-nowrap"
            data-testid="delete-now-button"
          >
            Delete forever now
          </Button>
        </div>
      </div>
    </div>
  );
};
